import React, { useEffect, useState } from "react";
import axios from "axios";
import { url } from "../../../store/initialStatus";
import { Translate } from "../../../helpers/Translate";

const t = new Translate();

const TRANSPORTATION_STATUS_COLORS: Record<string, string> = {
  student_leaves_alone_walking: "#dc3545",
  student_leaves_with_parent_in_car: "#0d6efd",
  with_parent_walking: "#198754",
  school_bus: "#ffc107",
};

const getTransportationTextColor = (status: string) =>
  status === "school_bus" ? "#212529" : "#ffffff";

const ParentCard = () => {
  const [guardian, setGuardian] = useState<any>(null);
  const [cardTemplate, setCardTemplate] = useState<any>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${url}guardian/getparentcard.php`, { withCredentials: true })
      .then((response) => {
        if (!mounted) return;
        if (response.data?.status !== "success" || !response.data?.data) {
          setError(response.data?.msg || t.translate("Unable to load parent card."));
          return;
        }
        setGuardian(response.data.data.guardian);
        setCardTemplate(response.data.data.cardtemplate);
      })
      .catch((requestError) => {
        if (mounted) {
          setError(
            requestError.response?.data?.msg ||
              t.translate("Unable to load parent card.")
          );
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }
  if (!guardian || !cardTemplate) {
    return <div>{t.translate("Loading...")}</div>;
  }

  const placeholderValue = (key: string) => {
    switch (key) {
      case "studentName":
        return `${guardian.firstname || ""} ${guardian.middlename || ""} ${
          guardian.lastname || ""
        }`.trim();
      case "studentId":
        return `ID: ${guardian.id || ""}`;
      case "studentImage":
        return guardian.driveid?.includes("https://")
          ? guardian.driveid
          : `https://drive.google.com/uc?export=view&id=${guardian.driveid}`;
      case "qrCode":
        return `https://cloud.ivyis.org/qr/?size=300&text=${encodeURIComponent(
          guardian.qrcode || ""
        )}&choe=UTF-8`;
      case "schoolInfo":
        return cardTemplate.school_name || "IVY International Schools";
      default:
        return "";
    }
  };

  const renderPlaceholder = (placeholder: any, index: number) => {
    const value = placeholderValue(placeholder.key);
    if (placeholder.type === "text") {
      const baseSize = Number(placeholder.fontSize) || 14;
      const width = Number(placeholder.width) || 0;
      const requiredWidth = value.length * baseSize * 0.35;
      const scale = width && requiredWidth > width ? width / requiredWidth : 1;
      const fontSize = Math.max(8, baseSize * scale);
      const fontFamily = placeholder.fontFamily?.name
        ? `ParentCardFont_${placeholder.key}_${index}`
        : "inherit";

      return (
        <React.Fragment key={index}>
          {placeholder.fontFamily?.source && (
            <style>{`@font-face { font-family: '${fontFamily}'; src: url('${placeholder.fontFamily.source}') format('truetype'); }`}</style>
          )}
          <div
            style={{
              position: "absolute",
              left: placeholder.x,
              top: placeholder.y,
              color: placeholder.color || "#000",
              fontSize,
              fontWeight: placeholder.fontWeight || 400,
              fontFamily,
              width: placeholder.width || "auto",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </div>
        </React.Fragment>
      );
    }
    if (placeholder.type === "image") {
      return (
        <img
          key={index}
          src={value || placeholder.source}
          alt={placeholder.label || placeholder.key}
          style={{
            position: "absolute",
            left: placeholder.x,
            top: placeholder.y,
            width: placeholder.width || "auto",
            height: placeholder.height || "auto",
            objectFit: "contain",
            aspectRatio: "1 / 1",
          }}
        />
      );
    }
    return null;
  };

  const width = Number(cardTemplate.card_width || cardTemplate.cardWidth) || 324;
  const height = Number(cardTemplate.card_height || cardTemplate.cardHeight) || 206;
  const students = Array.isArray(guardian.students) ? guardian.students : [];
  const face = (side: "front" | "back") => (
    <div
      style={{
        position: "relative",
        flex: `0 0 ${width}px`,
        width,
        height,
        backgroundImage: `url(${cardTemplate[`${side}image`]})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
      }}
    >
      {cardTemplate[`${side}_placeholders`]?.map(renderPlaceholder)}
      {side === "front" && students.length > 0 && (
        <div
          aria-label="Students' transportation statuses"
          style={{
            position: "absolute",
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 20,
            display: "flex",
            height: 20,
          }}
        >
          {students.map((student: any, index: number) => {
            const status = student.transportation_status || "";
            return (
              <div
                key={`${student.id || index}-${index}`}
                title={student.firstname || "Student"}
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  padding: "0 4px",
                  borderLeft:
                    index > 0
                      ? "1px solid rgba(255, 255, 255, 0.8)"
                      : "none",
                  backgroundColor:
                    TRANSPORTATION_STATUS_COLORS[status] || "#6c757d",
                  color: getTransportationTextColor(status),
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1,
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {student.firstname || "Student"}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ overflowX: "auto", padding: "10px 0 30px" }}>
      <div
        style={{
          display: "flex",
          gap: 20,
          flexWrap: "wrap",
          justifyContent: "center",
          minWidth: Math.min(width, 320),
        }}
      >
        {face("front")}
        {face("back")}
      </div>
    </div>
  );
};

export default ParentCard;
